import React, { useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  CONVERSATIONAL_AI_VALUE_STATUSES,
  CONVERSATIONAL_AI_MESSAGE_TYPES,
} from '../../../../constants';
import { FocusElement } from '../../../../helpers/focusElement';
import AiMessage from './chat/AiMessage';
import MessageLoader from './chat/MessageLoader';
import UserMessage from './chat/UserMessage';
import useConversationalAiPrompt from './hooks/useConversationalAiPrompt';
import useInitializeAi from './hooks/useInitializeAi';

function Conversational({ question }) {
  const {
    sessionId,
    loading: initializationLoading,
    error: initializationError,
  } = useInitializeAi(question);
  const {
    status,
    loading: promptLoading,
    dialog = [],
    fetchNextPrompt,
  } = useConversationalAiPrompt(sessionId);
  const aiInputRef = React.useRef(null);

  useEffect(() => {
    if (sessionId) {
      fetchNextPrompt('', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (!promptLoading && aiInputRef.current) {
      FocusElement($(aiInputRef.current));
      aiInputRef.current.focus();
    }
  }, [promptLoading]);

  const renderHiddenAnswers = useCallback(
    (currentQuestion, currentStatus) =>
      currentQuestion?.answers?.map((row) => (
        <div
          hidden
          className="answer--conversational-ai"
          id={row.id}
          data-row-id={row.id}
          data-row-ref-id={row.refId}
          data-column-id={currentQuestion?.matrix?.columns[0]?.id}
          data-column-ref-id={currentQuestion?.matrix?.columns[0]?.refId}
          data-column-order={1}
          data-input-required
          data-value="Initial value"
          data-status={currentStatus}
        ></div>
      )),
    [],
  );
  const onPaste = useCallback((event) => event.preventDefault(), []);
  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        const userInput = event.target.value.trim();
        if (userInput) {
          fetchNextPrompt(userInput);
          // eslint-disable-next-line no-param-reassign
          event.target.value = '';
        }
      }
    },
    [fetchNextPrompt],
  );
  const placeholderText = Pollfish.ui.getText('ai_input_placeholder');

  if (initializationError) {
    const errorMessage = Pollfish.ui.getText('conversational.error');
    return (
      <React.Fragment>
        <div className="flex-full-width">{errorMessage}</div>
        {renderHiddenAnswers(
          question,
          CONVERSATIONAL_AI_VALUE_STATUSES.FAILURE,
        )}
      </React.Fragment>
    );
  }

  return (
    <div className="flex-full-width">
      {dialog.map((item) => {
        if (item.type === CONVERSATIONAL_AI_MESSAGE_TYPES.USER) {
          return <UserMessage key={item.message} text={item.message} />;
        }

        return (
          <AiMessage
            key={item.message}
            visible={item.visible}
            text={item.message}
          />
        );
      })}

      {(promptLoading || initializationLoading) && (
        <AiMessage visible text={<MessageLoader />} />
      )}
      {!promptLoading &&
        !initializationLoading &&
        status === CONVERSATIONAL_AI_VALUE_STATUSES.ONGOING_CONVERSATION && (
          <div className="user-input-ai-wrapper">
            <textarea
              ref={aiInputRef}
              className="user-input-ai"
              rows={1}
              placeholder={placeholderText}
              aria-label={placeholderText}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
            />
            <span
              className={classNames('user-input-ai-icon', 'icon-enter-sharp')}
            ></span>
          </div>
        )}
      {renderHiddenAnswers(question, status)}
    </div>
  );
}

Conversational.propTypes = {
  question: PropTypes.object.isRequired,
};

export default Conversational;
